// material
import { Container } from '@mui/material';
// hooks
import useSettings from '../../hooks/useSettings';
// components
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { PATH_DASHBOARD } from '../../routes/paths';
import { checkRoles, ROLE_USER } from 'guards/CheckRoles';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../utils/axios';
import { useEffect, useState } from 'react';
import DeliveryConfigurationForm from '../../components/delivery-configuration/DeliveryConfigurationForm';
import { MIconButton } from '../../components/@material-extend';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import { useSnackbar } from 'notistack';
import LoadingSpinner from '../../components/LoadingSpinner';
import { getProfile } from '../../redux/slices/user';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { DeliveryConfiguration } from '../../@types/model/deliveryConfiguration';

// ----------------------------------------------------------------------
export type RootStackParamList = {
  EditDeliveryConfiguration: { addIndex: boolean };
};

export default function EditDeliveryConfiguration() {
  const { themeStretch } = useSettings();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const applicationDispatch = useDispatch();
  const { loaded, deliveryConfigurations, user } = useSelector((state: RootState) => state.user);
  const [deliveryConfiguration, setDeliveryConfiguration] = useState<DeliveryConfiguration | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const { guid } = useParams();
  const role = checkRoles(user, ROLE_USER) ? '/' : '/end-user';

  useEffect(() => {
    if (loaded) {
      setDeliveryConfiguration(
        deliveryConfigurations.find((dt: DeliveryConfiguration) => dt.guid === guid)!
      );
    }
  }, [loaded]);

  const saveDeliveryConfiguration = (deliveryConfiguration: DeliveryConfiguration) => {
    setLoading(true);
    const data = {
      ...deliveryConfiguration,
      device_types: [deliveryConfiguration.configuration.device_type],
      device_tags: []
    };
    axios.put(`/api/v2/delivery-configuration/${deliveryConfiguration.guid}`, data).then(() => {
      setDeliveryConfiguration(deliveryConfiguration);
      enqueueSnackbar(t('delivery_configuration_updated'), {
        variant: 'success',
        action: (key) => (
          <MIconButton size="small" onClick={() => closeSnackbar(key)}>
            <Icon icon={closeFill} />
          </MIconButton>
        )
      });

      applicationDispatch(getProfile());

      navigate(generatePath(PATH_DASHBOARD.deliveryConfigurations.list));
      setLoading(false);
    });
  };
  const pageTitle = t('edit_delivery_configuration');
  return (
    <Page title={pageTitle}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          heading={pageTitle}
          links={[
            { name: t('home'), href: role },

            { name: t('home'), href: PATH_DASHBOARD.end_user.dashboard },
            {
              name: t('delivery_configurations'),
              href: PATH_DASHBOARD.deliveryConfigurations.list
            },
            { name: pageTitle, href: PATH_DASHBOARD.deliveryConfigurations.new }
          ]}
        />
        {!loaded || !deliveryConfiguration ? (
          <LoadingSpinner />
        ) : (
          <DeliveryConfigurationForm
            deliveryConfiguration={deliveryConfiguration}
            edit={true}
            submitHandler={saveDeliveryConfiguration}
            loading={loading}
          />
        )}
      </Container>
    </Page>
  );
}
